
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';

interface BannerDismissalRecord {
  dismissed: boolean;
  versionHash: string;
}

export enum Severity {
  Info = 'info',
  Low = 'low',
  Medium = 'medium',
  High = 'high',
}

@Component({
  selector: 'itc-global-banner',
  templateUrl: './global-banner.component.html',
  styleUrls: ['./global-banner.component.scss'],
  imports: [MatIconModule, MatButtonModule, RouterModule],
})
export class GlobalBannerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() releaseId: string;
  @Input() severity = 'medium';
  @Output() dismissClicked: EventEmitter<any> = new EventEmitter();
  @Input() text: string = '';
  @Input() linkText: string = '';
  @Input() identifier: string = '';
  @Input() linkParams: string[];
  icon: string = '';
  displayed = false;

  routerLink: string[];

  constructor() {}

  ngOnInit(): void {
    this.updatePresentationState();
    window.addEventListener('storage', this.storageEventListener);
  }

  ngOnChanges(): void {
    this.updatePresentationState();
  }

  ngOnDestroy(): void {
    window.removeEventListener('storage', this.storageEventListener);
  }

  onDismiss(): void {
    this.dismissClicked.emit();

    localStorage.setItem(
      this.getStorageKey(),
      JSON.stringify({
        dismissed: true,
        versionHash: this.getCurrentVersionHash(),
      } satisfies BannerDismissalRecord)
    );

    this.displayed = false;
  }

  private onStorageChange(event: StorageEvent): void {
    if (event.key === this.getStorageKey()) {
      this.updateDismissalState();
    }
  }

  private updatePresentationState(): void {
    this.updateIcon();
    this.updateRouterLink();
    this.updateDismissalState();
  }

  private updateIcon(): void {
    switch (this.severity) {
      case Severity.Info:
        this.icon = 'info_outline';
        break;
      case Severity.Low:
        this.icon = 'campaign';
        break;
      case Severity.Medium:
        this.icon = 'error_outline';
        break;
      case Severity.High:
        this.icon = 'warning_amber';
        break;
      default:
        this.icon = '';
        this.text = '';
        break;
    }
  }

  private updateRouterLink(): void {
    this.routerLink = [
      '/help/support-center',
      ...this.normalizeLinkParams(this.linkParams),
    ];
  }

  private updateDismissalState(): void {
    const dismissalRecord = this.readDismissalRecord();

    this.displayed =
      !dismissalRecord?.dismissed ||
      dismissalRecord.versionHash !== this.getCurrentVersionHash();
  }

  private readDismissalRecord(): BannerDismissalRecord | null {
    const record = localStorage.getItem(this.getStorageKey());

    if (!record) {
      return null;
    }

    try {
      const parsedRecord = JSON.parse(record) as Partial<BannerDismissalRecord>;

      if (
        parsedRecord?.dismissed === true &&
        typeof parsedRecord.versionHash === 'string' &&
        parsedRecord.versionHash !== ''
      ) {
        return {
          dismissed: true,
          versionHash: parsedRecord.versionHash,
        };
      }
    } catch {
      return null;
    }

    return null;
  }

  private getCurrentVersionHash(): string {
    return this.hashString(this.getCanonicalInput());
  }

  private getCanonicalInput(): string {
    return JSON.stringify({
      releaseId: this.normalizeScalar(this.releaseId),
      severity: this.normalizeScalar(this.severity),
      text: this.normalizeScalar(this.text),
      linkText: this.normalizeScalar(this.linkText),
      identifier: this.normalizeScalar(this.identifier),
      linkParams: this.normalizeLinkParams(this.linkParams).join('|'),
    });
  }

  private normalizeScalar(value: string | null | undefined): string {
    return value ?? '';
  }

  private normalizeLinkParams(
    value: string[] | null | undefined
  ): string[] {
    if (!value?.length) {
      return [];
    }

    return value.map((item) => this.normalizeScalar(item));
  }

  private hashString(value: string): string {
    // Use a lightweight 32-bit FNV-1a hash: fast enough for UI state versioning
    // while keeping collision risk low for the small set of banner text changes.
    // 2166136261 is the 32-bit FNV offset basis; 16777619 is the 32-bit FNV prime.
    let hash = 2166136261;

    for (let index = 0; index < value.length; index++) {
      hash ^= value.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }

    return (hash >>> 0).toString(16).padStart(8, '0');
  }

  private getStorageKey(): string {
    return `display-banner-v2-${this.normalizeScalar(this.identifier)}`;
  }

  private readonly storageEventListener = (event: StorageEvent): void => this.onStorageChange(event);
}
